import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createQuoteStream } from "~/api/streaming";
import { api } from "~/api/client";
import { useDebounce } from "./useDebounce";
import { REFRESH_INTERVAL_MS } from "~/lib/constants";
import type { Route } from "~/api/types";
import type { QuoteStream } from "~/api/streaming";

export interface StreamingRoutesParams {
  fromChainId: string;
  toChainId: string;
  fromToken: string;
  toToken: string;
  amount: string;
  sender?: string;
  recipient?: string;
  slippageBps?: number;
}

export interface StreamingRoutesResult {
  routes: Route[];
  pending: string[];
  isStreaming: boolean;
  isDone: boolean;
  error: Error | null;
  quoteId: string | null;
  secondsUntilRefresh: number | null;
  refresh: () => void;
}

export function useStreamingRoutes(
  params: StreamingRoutesParams | null,
  opts?: { paused?: boolean },
): StreamingRoutesResult {
  const paused = opts?.paused ?? false;
  const debouncedParams = useDebounce(params, 300);
  const queryClient = useQueryClient();
  const streamRef = useRef<QuoteStream | null>(null);

  // Stable key so the effect only re-runs when params actually change
  const paramsKey = useMemo(
    () => (debouncedParams ? JSON.stringify(debouncedParams) : null),
    [debouncedParams],
  );

  const [routes, setRoutes] = useState<Route[]>([]);
  const [pending, setPending] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [quoteId, setQuoteId] = useState<string | null>(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Track whether this is a refresh (same params) vs new query
  const prevParamsKeyRef = useRef<string | null>(null);
  const isRefreshRef = useRef(false);

  const closeStream = useCallback(() => {
    streamRef.current?.close();
    streamRef.current = null;
  }, []);

  // Manual refresh callback
  const refresh = useCallback(() => {
    setSecondsUntilRefresh(null);
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  // ── Streaming effect ──
  useEffect(() => {
    const isRefresh = paramsKey != null && paramsKey === prevParamsKeyRef.current;
    prevParamsKeyRef.current = paramsKey;
    isRefreshRef.current = isRefresh;

    closeStream();

    if (!isRefresh) {
      setRoutes([]);
      setPending([]);
      setError(null);
      setQuoteId(null);
    }
    setIsDone(false);

    if (!paramsKey || !debouncedParams) {
      setIsStreaming(false);
      return;
    }

    const { fromChainId, toChainId, fromToken, toToken, amount, sender, slippageBps } =
      debouncedParams;

    if (!fromChainId || !toChainId || !fromToken || !toToken || !amount) {
      setIsStreaming(false);
      return;
    }

    setIsStreaming(true);

    const queryKey = ["routes", fromChainId, toChainId, fromToken, toToken, amount, sender];

    let usedFallback = false;

    const { recipient } = debouncedParams;

    const stream = createQuoteStream({
      fromChainId,
      toChainId,
      fromToken,
      toToken,
      amount,
      recipient: recipient ?? sender,
      sender,
      slippageBps,
    });

    stream
      .onRoutes((event) => {
        setQuoteId(event.quoteId);
        // During refresh, keep old routes visible and batch-update on done
        if (!isRefreshRef.current) {
          setRoutes(event.routes);
          setPending(event.pending);
        }
        queryClient.setQueryData(queryKey, event.routes);
      })
      .onDone((event) => {
        setRoutes(event.routes);
        setPending([]);
        setIsDone(true);
        setIsStreaming(false);
        isRefreshRef.current = false;
        queryClient.setQueryData(queryKey, event.routes);
      })
      .onError((err) => {
        // During a refresh, silently keep old routes and restart countdown
        if (isRefreshRef.current) {
          isRefreshRef.current = false;
          setIsDone(true);
          setIsStreaming(false);
          return;
        }

        // If SSE fails entirely (connection refused, proxy issue), fall back to POST
        if (!usedFallback && err instanceof Error) {
          usedFallback = true;
          api
            .getQuote({
              fromChainId,
              toChainId,
              fromToken,
              toToken,
              amount,
              recipient: recipient ?? sender,
              sender,
              slippageBps,
            })
            .then((res) => {
              setQuoteId(res.quoteId);
              setRoutes(res.routes);
              setPending([]);
              setIsDone(true);
              setIsStreaming(false);
              queryClient.setQueryData(queryKey, res.routes);
            })
            .catch((fallbackErr) => {
              setError(
                fallbackErr instanceof Error ? fallbackErr : new Error("Quote request failed"),
              );
              setIsStreaming(false);
            });
          return;
        }

        const msg =
          err instanceof Error
            ? err
            : new Error(
                err && typeof err === "object" && "message" in err
                  ? (err as { message: string }).message
                  : String(err),
              );
        setError(msg);
        setIsStreaming(false);
      });

    streamRef.current = stream;

    return () => {
      closeStream();
    };
  }, [paramsKey, refreshTrigger, queryClient, closeStream]);

  // ── Countdown effect ──
  useEffect(() => {
    if (!isDone || paused || !paramsKey) {
      setSecondsUntilRefresh(null);
      return;
    }

    let remaining = Math.round(REFRESH_INTERVAL_MS / 1000);
    setSecondsUntilRefresh(remaining);

    const tick = setInterval(() => {
      remaining -= 1;
      setSecondsUntilRefresh(remaining);
      if (remaining <= 0) {
        clearInterval(tick);
        setRefreshTrigger((prev) => prev + 1);
      }
    }, 1000);

    return () => {
      clearInterval(tick);
      setSecondsUntilRefresh(null);
    };
  }, [isDone, paused, paramsKey]);

  return { routes, pending, isStreaming, isDone, error, quoteId, secondsUntilRefresh, refresh };
}
